import { useState, useEffect } from 'react'
import {
	Box,
	Button,
	TextField,
	Typography,
	Alert,
	CircularProgress,
	Paper,
	IconButton,
	InputAdornment
} from '@mui/material'
import { useNavigate, useSearchParams } from 'react-router-dom'
import useLogin from '../hooks/useLogin'
import { useAppSelector } from '../redux/hooks'
import { ArrowBack, Visibility, VisibilityOff, Lock } from '@mui/icons-material'

export const ResetPassword = () => {
	const { resetPasswordHandler } = useLogin()
	const { loading } = useAppSelector((state) => state.auth)
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()

	const [newPassword, setNewPassword] = useState('')
	const [confirmPassword, setConfirmPassword] = useState('')
	const [showPassword, setShowPassword] = useState(false)
	const [showConfirmPassword, setShowConfirmPassword] = useState(false)
	const [message, setMessage] = useState('')
	const [error, setError] = useState('')
	const [passwordReset, setPasswordReset] = useState(false)
	const [token, setToken] = useState('')

	useEffect(() => {
		const tokenFromUrl = searchParams.get('token')
		if (!tokenFromUrl) {
			setError('Token inválido. Por favor, solicita un nuevo enlace de recuperación.')
		} else {
			setToken(tokenFromUrl)
		}
	}, [searchParams])

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		
		if (!newPassword || !confirmPassword) {
			setError('Por favor, completa todos los campos.')
			return
		}

		if (newPassword.length < 6) {
			setError('La contraseña debe tener al menos 6 caracteres.')
			return
		}

		if (newPassword !== confirmPassword) {
			setError('Las contraseñas no coinciden.')
			return
		}

		setError('')
		setMessage('')

		const result = await resetPasswordHandler(token, newPassword)
		
		if (result.success) {
			setPasswordReset(true)
			setMessage(result.message)
		} else {
			setError(result.message)
		}
	}

	const handleBackToLogin = () => {
		navigate('/login')
	}

	if (!token && !error) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
				<CircularProgress />
			</Box>
		)
	}

	return (
		<Box 
			display="flex" 
			justifyContent="center" 
			alignItems="center" 
			minHeight="100vh"
			sx={{ backgroundColor: '#f5f5f5', padding: 2 }}
		>
			<Paper 
				elevation={6} 
				sx={{ 
					padding: 4, 
					maxWidth: 500, 
					width: '100%',
					borderRadius: 2
				}}
			>
				<Box display="flex" justifyContent="center" marginBottom={3}>
					<img src="/assets/ICTALogo.png" alt="ICTA Logo" width="120" height="120" />
				</Box>

				<Typography variant="h4" component="h1" gutterBottom textAlign="center">
					Restablecer Contraseña
				</Typography>

				{!passwordReset && token ? (
					<>
						<Typography variant="body1" color="textSecondary" textAlign="center" marginBottom={3}>
							Ingresa tu nueva contraseña.
						</Typography>

						<Box component="form" onSubmit={handleSubmit} width="100%">
							<TextField
								fullWidth
								label="Nueva Contraseña"
								type={showPassword ? 'text' : 'password'}
								value={newPassword}
								onChange={(e) => setNewPassword(e.target.value)}
								variant="outlined"
								margin="normal"
								required
								disabled={loading}
								InputProps={{
									startAdornment: <Lock sx={{ color: 'action.active', mr: 1 }} />,
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												onClick={() => setShowPassword(!showPassword)}
												edge="end"
											>
												{showPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
										</InputAdornment>
									)
								}}
							/>

							<TextField
								fullWidth
								label="Confirmar Nueva Contraseña"
								type={showConfirmPassword ? 'text' : 'password'}
								value={confirmPassword}
								onChange={(e) => setConfirmPassword(e.target.value)}
								variant="outlined"
								margin="normal"
								required
								disabled={loading}
								InputProps={{
									startAdornment: <Lock sx={{ color: 'action.active', mr: 1 }} />,
									endAdornment: (
										<InputAdornment position="end">
											<IconButton
												onClick={() => setShowConfirmPassword(!showConfirmPassword)}
												edge="end"
											>
												{showConfirmPassword ? <VisibilityOff /> : <Visibility />}
											</IconButton>
										</InputAdornment>
									)
								}}
							/>

							{error && (
								<Alert severity="error" sx={{ marginY: 2 }}>
									{error}
								</Alert>
							)}

							<Button
								type="submit"
								fullWidth
								variant="contained"
								size="large"
								disabled={loading}
								sx={{ 
									marginY: 2,
									backgroundColor: '#177C3B',
									'&:hover': {
										backgroundColor: '#145a2e'
									}
								}}
							>
								{loading ? (
									<CircularProgress size={24} color="inherit" />
								) : (
									'Restablecer Contraseña'
								)}
							</Button>
						</Box>
					</>
				) : passwordReset ? (
					<Box textAlign="center">
						<Alert severity="success" sx={{ marginY: 2 }}>
							{message}
						</Alert>
						
						<Typography variant="body1" color="textSecondary" marginY={2}>
							Tu contraseña ha sido restablecida exitosamente. Ahora puedes iniciar sesión con tu nueva contraseña.
						</Typography>
					</Box>
				) : (
					<Box textAlign="center">
						<Alert severity="error" sx={{ marginY: 2 }}>
							{error}
						</Alert>
					</Box>
				)}

				<Box display="flex" justifyContent="center" marginTop={3}>
					<Button
						startIcon={<ArrowBack />}
						onClick={handleBackToLogin}
						variant="text"
						sx={{ color: '#177C3B' }}
					>
						Volver al Login
					</Button>
				</Box>
			</Paper>
		</Box>
	)
}
